import { Box, Chip, Typography, useTheme } from '@mui/material';
import { Check, CheckCircle, X } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

type PublishedStatusProps = {
  isPublished: boolean;
};

export const PublishedStatus = ({ isPublished }: PublishedStatusProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isPublished ? (
        <CheckCircle size={16} color={palette.success.main} />
      ) : (
        <X size={16} color={palette.error.main} />
      )}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 'medium',
        }}
      >
        {isPublished ? t('Published') : t('Unpublished')}
      </Typography>
    </Box>
  );
};

type PublishedChipProps = {
  isPublished: boolean;
  size?: 'small' | 'medium';
};

export const PublishedChip = ({
  isPublished,
  size = 'small',
}: PublishedChipProps) => {
  const { t } = useLocale();

  return (
    <Chip
      size="small"
      icon={
        isPublished ? (
          <Check size={14} strokeWidth={3} />
        ) : (
          <X size={14} strokeWidth={3} />
        )
      }
      label={isPublished ? t('Published') : t('Unpublished')}
      sx={theme => ({
        fontWeight: 600,
        fontSize: '0.75rem',
        height: size === 'small' ? 22 : 24,
        borderRadius: '6px',
        '& .MuiChip-icon': {
          ml: 0.5,
          mr: -0.25,
          fontSize: 14,
          color: 'inherit',
        },
        ...(isPublished
          ? {
              bgcolor: `color-mix(in srgb, ${
                (theme.vars ?? theme).palette.success.main
              } 8%, transparent)`,
              color: (theme.vars ?? theme).palette.success.dark,
            }
          : {
              bgcolor: `color-mix(in srgb, ${
                (theme.vars ?? theme).palette.error.main
              } 8%, transparent)`,
              color: (theme.vars ?? theme).palette.error.dark,
            }),
      })}
    />
  );
};
